import React, { useState, useEffect } from 'react';
import '../styles/Team.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed  = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const headers = { "Content-Type": "application/json" };
      if (email) headers["x-user-email"] = email;
      return headers;
    }
  } catch { /* ignore */ }
  return { "Content-Type": "application/json" };
}

const PERMISSIONS = [
  { key: 'can_view_data',    label: 'View audit data' },
  { key: 'can_run_sync',     label: 'Run syncs' },
  { key: 'can_register',     label: 'Register new sources' },
  { key: 'can_manage_hsn',   label: 'Manage HSN codes' },
  { key: 'can_view_reports', label: 'View reports' },
];

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="tm-toggle"
      style={{ background: checked ? 'var(--accent)' : 'var(--bg3)' }}
    >
      <div
        className="tm-toggle-knob"
        style={{ left: checked ? 19 : 3 }}
      />
    </button>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending:  { label: 'Pending',  color: 'var(--amber)', bg: 'var(--amber-bg)' },
    active:   { label: 'Active',   color: 'var(--green)', bg: 'var(--green-bg)' },
    rejected: { label: 'Declined', color: 'var(--red)',   bg: 'var(--red-bg)'   },
  };
  const c = config[status] || config.pending;
  return (
    <span
      className="tm-status-badge"
      style={{ background: c.bg, color: c.color }}
    >
      {c.label}
    </span>
  );
}

function MemberCard({ member, onRemove, onPermissionChange, allSources }) {
  const [perms, setPerms] = useState({
    can_view_data:    member.can_view_data,
    can_run_sync:     member.can_run_sync,
    can_register:     member.can_register,
    can_manage_hsn:   member.can_manage_hsn,
    can_view_reports: member.can_view_reports,
    access_type:      member.access_type     || 'complete',
    allowed_sources:  member.allowed_sources || [],
  });

  const displayName = member.full_name || member.invited_email;

  const handleToggle = (key, value) => {
    const updated = { ...perms, [key]: value };
    setPerms(updated);
    onPermissionChange(member.id, updated);
  };

  const handleAccessTypeChange = (type) => {
    const updated = { ...perms, access_type: type, allowed_sources: type === 'complete' ? [] : perms.allowed_sources };
    setPerms(updated);
    onPermissionChange(member.id, updated);
  };

  const handleSourceToggle = (sourceId) => {
    const current = perms.allowed_sources || [];
    const updated = current.includes(sourceId)
      ? current.filter(id => id !== sourceId)
      : [...current, sourceId];
    const newPerms = { ...perms, allowed_sources: updated };
    setPerms(newPerms);
    onPermissionChange(member.id, newPerms);
  };

  return (
    <div className="tm-card">
      <div className="tm-card-header">
        <div>
          <div className="tm-member-name">{displayName}</div>
          <div className="tm-member-email">{member.invited_email}</div>
          {member.accepted_at && (
            <div className="tm-member-joined">
              Joined {new Date(member.accepted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          )}
        </div>
        <div className="tm-card-actions">
          <StatusBadge status={member.status} />
          <button className="tm-btn-remove" onClick={() => onRemove(member.id)}>
            Remove
          </button>
        </div>
      </div>

      {member.status === 'active' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <div className="tm-section-label">Source Access</div>
            <div
              className="tm-access-btns"
              style={{ marginBottom: perms.access_type === 'specific' ? 10 : 0 }}
            >
              {['complete', 'specific'].map(type => (
                <button
                  key={type}
                  onClick={() => handleAccessTypeChange(type)}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: '1px solid',
                    borderColor: perms.access_type === type ? 'var(--accent)' : 'var(--border)',
                    background:  perms.access_type === type ? 'var(--accent)' : 'transparent',
                    color:       perms.access_type === type ? '#fff' : 'var(--text3)',
                    fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)',
                    textTransform: 'capitalize',
                  }}
                >
                  {type === 'complete' ? '🔓 All Sources' : '🔒 Specific Sources'}
                </button>
              ))}
            </div>

            {perms.access_type === 'specific' && (
              <div className="tm-source-selector">
                {allSources.length === 0 ? (
                  <div className="tm-source-empty">No sources available</div>
                ) : allSources.map(s => (
                  <div key={s.id} className="tm-source-row">
                    <span className="tm-source-name">{s.name}</span>
                    <Toggle
                      checked={(perms.allowed_sources || []).includes(s.id)}
                      onChange={() => handleSourceToggle(s.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="tm-section-label">Feature Permissions</div>
            <div className="tm-perms-list">
              {PERMISSIONS.map(p => (
                <div key={p.key} className="tm-perm-row">
                  <span className="tm-perm-label">{p.label}</span>
                  <Toggle checked={perms[p.key]} onChange={val => handleToggle(p.key, val)} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {member.status === 'pending' && (
        <div className="tm-pending-note">
          Waiting for {member.invited_email} to accept the invitation.
        </div>
      )}
    </div>
  );
}

export default function Team({ userRole }) {
  const [members,     setMembers]     = useState([]);
  const [sources,     setSources]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting,    setInviting]    = useState(false);
  const [inviteMsg,   setInviteMsg]   = useState('');
  const [inviteError, setInviteError] = useState('');

  const isCA         = userRole?.isCA         || false;
  const isTeamMember = userRole?.isTeamMember || false;

  const loadData = async () => {
    setLoading(true); setError('');
    try {
      const [membersRes, sourcesRes] = await Promise.all([
        fetch(`${BASE}/users/team`,  { headers: getAuthHeaders() }),
        fetch(`${BASE}/sources`,     { headers: getAuthHeaders() }),
      ]);
      const membersData = await membersRes.json();
      const sourcesData = await sourcesRes.json();
      if (!membersRes.ok) throw new Error(membersData.error || 'Failed to load team');
      setMembers(membersData);
      setSources(Array.isArray(sourcesData) ? sourcesData : []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isCA && !isTeamMember) loadData(); }, [isCA, isTeamMember]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { setInviteError('Please enter an email address'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) { setInviteError('Please enter a valid email address'); return; }
    setInviting(true); setInviteError(''); setInviteMsg('');
    try {
      const res  = await fetch(`${BASE}/users/team/invite`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ invitedEmail: inviteEmail.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invite');
      setInviteMsg(data.message); setInviteEmail('');
      loadData();
    } catch (err) { setInviteError(err.message); }
    finally { setInviting(false); }
  };

  const handleRemove = async (inviteId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      const res  = await fetch(`${BASE}/users/team/${inviteId}`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove');
      setMembers(prev => prev.filter(m => m.id !== inviteId));
    } catch (err) { setError(err.message); }
  };

  const handlePermissionChange = async (inviteId, permissions) => {
    try {
      const res = await fetch(`${BASE}/users/team/${inviteId}/permissions`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(permissions),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
    } catch (err) { setError('Failed to save: ' + err.message); }
  };

  if (!isCA || isTeamMember) {
    return (
      <div className="tm-unauthorized">
        CA access required to manage team members.
      </div>
    );
  }

  const active  = members.filter(m => m.status === 'active');
  const pending = members.filter(m => m.status === 'pending');

  return (
    <div className="tm-page">
      <h2 className="tm-title">Team Members</h2>
      <p className="tm-sub">
        Invite team members and control their access to sources and features.
      </p>

      <div className="tm-invite-card">
        <div className="tm-invite-title">Invite a team member</div>
        <div className="tm-invite-row">
          <input
            type="email"
            placeholder="colleague@example.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleInvite()}
            className="tm-invite-input"
          />
          <button onClick={handleInvite} disabled={inviting} className="tm-btn-invite">
            {inviting ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
        {inviteError && <div className="tm-invite-error">{inviteError}</div>}
        {inviteMsg   && <div className="tm-invite-success">✓ {inviteMsg}</div>}
        <div className="tm-invite-hint">
          They will receive an email invitation and can accept from their Profile page.
          You can control source access and feature permissions after they accept.
        </div>
      </div>

      {error && <div className="tm-error-banner">{error}</div>}

      {active.length > 0 && (
        <div className="tm-group">
          <div className="tm-group-label">Active ({active.length})</div>
          {active.map(m => (
            <MemberCard
              key={m.id}
              member={m}
              onRemove={handleRemove}
              onPermissionChange={handlePermissionChange}
              allSources={sources}
            />
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="tm-group">
          <div className="tm-group-label">Pending Invites ({pending.length})</div>
          {pending.map(m => (
            <MemberCard
              key={m.id}
              member={m}
              onRemove={handleRemove}
              onPermissionChange={handlePermissionChange}
              allSources={sources}
            />
          ))}
        </div>
      )}

      {!loading && members.length === 0 && (
        <div className="tm-empty">
          <div className="tm-empty-icon">👥</div>
          <div>No team members yet.</div>
          <div className="tm-empty-hint">Invite a colleague using the form above.</div>
        </div>
      )}

      {loading && <div className="tm-loading">Loading…</div>}
    </div>
  );
}
